import { useState, useEffect, useRef } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, Heart, MessageCircle, Plus, Image, Send, Trash2, X, User, Upload, 
  Loader2, Edit2, Check, ChevronLeft, ChevronRight, Settings, Camera, 
  MessageSquare, Users, Clock, Eye, Pin, ChevronDown, Search, ShoppingBag,
  PoundSterling, FileText, Paperclip, MapPin, Mail, Star, CheckCircle, Tag
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_CATEGORIES = [
  "Bonnet Stripes",
  "General Discussion",
  "Mini Related for Sale",
  "Roof Graphics",
  "Showcase Your Mini",
  "Side Stripes",
  "Tips & Tricks"
];

export const MiniStripesOwnersClub = ({ initialView }) => {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const commentFileInputRef = useRef(null);
  
  // View mode: 'forum', 'gallery', 'profile', or 'marketplace'
  // Check URL params or initialView prop for initial view mode
  const getInitialViewMode = () => {
    if (initialView) return initialView;
    const viewParam = searchParams.get('view');
    if (viewParam === 'forsale') return 'marketplace';
    // Default to forum initially, will switch to profile when user loads
    return 'forum';
  };
  const [viewMode, setViewMode] = useState(getInitialViewMode());
  const [hasSetInitialProfile, setHasSetInitialProfile] = useState(false);
  
  // Switch to profile view when user first loads (only once, and only on main page)
  useEffect(() => {
    if (user && !hasSetInitialProfile && !initialView) {
      setViewMode('profile');
      setHasSetInitialProfile(true);
    }
  }, [user, hasSetInitialProfile, initialView]);
  
  // Switch to forum if user logs out while on profile view
  useEffect(() => {
    if (viewMode === 'profile' && !user) {
      setViewMode('forum');
    }
  }, [user, viewMode]);
  const [userPosts, setUserPosts] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  
  // Handle URL parameter changes
  useEffect(() => {
    if (initialView) {
      setViewMode(initialView);
    } else {
      const viewParam = searchParams.get('view');
      if (viewParam === 'forsale') {
        setViewMode('marketplace');
      }
    }
  }, [searchParams, initialView]);
  
  // Image gallery lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Categories - combine default with custom from posts
  const [customCategories, setCustomCategories] = useState([]);
  const [newCustomCategory, setNewCustomCategory] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);

  // Create post form
  const [newPost, setNewPost] = useState({
    title: "",
    content: "",
    category: "",
    image_urls: [],
    price: "",  // For marketplace items
    location: "",  // For marketplace items
    forsale_category: ""  // For Sale sub-category
  });
  const [imageUrl, setImageUrl] = useState("");
  
  // For Sale categories
  const [forsaleCategories, setForsaleCategories] = useState([]);
  const [selectedForsaleCategory, setSelectedForsaleCategory] = useState("");
  
  // Messaging state
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messageContent, setMessageContent] = useState("");
  const [messageListing, setMessageListing] = useState(null);

  // Seller Rating state
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [ratingTarget, setRatingTarget] = useState(null);
  const [ratingValue, setRatingValue] = useState(5);
  const [ratingComment, setRatingComment] = useState("");
  const [sellerRatings, setSellerRatings] = useState({});
  const [submittingRating, setSubmittingRating] = useState(false);

  // Comment form
  const [newComment, setNewComment] = useState("");
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [uploadingComment, setUploadingComment] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editCommentText, setEditCommentText] = useState("");

  // Delete confirmation modal state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ type: null, id: null });

  // Category translation mapping
  const categoryTranslationKeys = {
    "Bonnet Stripes": "bonnetStripes",
    "General Discussion": "generalDiscussion",
    "Roof Graphics": "roofGraphics",
    "Showcase Your Mini": "showcaseYourMini",
    "Side Stripes": "sideStripes",
    "Tips & Tricks": "tipsAndTricks"
  };

  // Translate category name
  const translateCategory = (category) => {
    const key = categoryTranslationKeys[category];
    return key ? t(key) : category;
  };

  // Get all unique categories (default + custom from posts) sorted alphabetically
  const getAllCategories = () => {
    const allCats = new Set([...DEFAULT_CATEGORIES, ...customCategories]);
    posts.forEach(post => {
      if (post.category) allCats.add(post.category);
    });
    return Array.from(allCats).sort((a, b) => a.localeCompare(b));
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 12 });
      if (selectedCategory) params.append("category", selectedCategory);
      
      const response = await fetch(`${API}/community/posts?${params}`);
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts);
        setTotalPages(data.total_pages);
        
        // Extract custom categories from posts
        const postCategories = data.posts
          .map(p => p.category)
          .filter(c => c && !DEFAULT_CATEGORIES.includes(c));
        setCustomCategories(prev => [...new Set([...prev, ...postCategories])]);
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  // Search topics
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (!query.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await fetch(`${API}/community/posts?limit=50`);
      if (response.ok) {
        const data = await response.json();
        const results = data.posts.filter(post => 
          post.title.toLowerCase().includes(query.toLowerCase()) ||
          post.content.toLowerCase().includes(query.toLowerCase()) ||
          post.category?.toLowerCase().includes(query.toLowerCase()) ||
          post.user_name?.toLowerCase().includes(query.toLowerCase())
        );
        setSearchResults(results);
      }
    } catch (error) {
      console.error("Error searching posts:", error);
    }
  };

  // Add custom category
  const handleAddCustomCategory = () => {
    if (newCustomCategory.trim() && !getAllCategories().includes(newCustomCategory.trim())) {
      setCustomCategories(prev => [...prev, newCustomCategory.trim()]);
      setNewPost({ ...newPost, category: newCustomCategory.trim() });
      setNewCustomCategory("");
      setShowAddCategory(false);
      toast.success("Category added!");
    }
  };

  // Fetch For Sale categories
  useEffect(() => {
    const fetchForsaleCategories = async () => {
      try {
        const response = await fetch(`${API}/community/forsale-categories`);
        if (response.ok) {
          const data = await response.json();
          setForsaleCategories(data);
        }
      } catch (error) {
        console.error("Error fetching for sale categories:", error);
      }
    };
    fetchForsaleCategories();
  }, []);

  // Mark listing as sold
  const handleMarkSold = async (postId, isSold) => {
    if (!token) {
      toast.error("Please login");
      return;
    }
    try {
      const endpoint = isSold ? 'unsold' : 'sold';
      const response = await fetch(`${API}/community/posts/${postId}/${endpoint}`, {
        method: "PUT",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      if (response.ok) {
        toast.success(isSold ? "Listing marked as available" : "Listing marked as sold");
        fetchPosts();
        if (selectedPost?.id === postId) {
          setSelectedPost(prev => ({ ...prev, is_sold: !isSold }));
        }
      }
    } catch (error) {
      toast.error("Failed to update listing");
    }
  };

  // Send message to seller
  const handleSendMessage = async () => {
    if (!token) {
      toast.error("Please login to send messages");
      return;
    }
    if (!messageContent.trim()) {
      toast.error("Please enter a message");
      return;
    }
    try {
      const response = await fetch(`${API}/community/messages`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          recipient_id: messageRecipient.id,
          listing_id: messageListing?.id,
          content: messageContent
        })
      });
      if (response.ok) {
        toast.success("Message sent!");
        setShowMessageModal(false);
        setMessageContent("");
        setMessageRecipient(null);
        setMessageListing(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send message");
      }
    } catch (error) {
      toast.error("Failed to send message");
    }
  };

  // Open message modal
  const openMessageModal = (recipientId, recipientName, listing = null) => {
    setMessageRecipient({ id: recipientId, name: recipientName });
    setMessageListing(listing);
    setShowMessageModal(true);
  };

  // Fetch seller ratings
  const fetchSellerRatings = async (sellerId) => {
    try {
      const response = await fetch(`${API}/community/sellers/${sellerId}/ratings`);
      if (response.ok) {
        const data = await response.json();
        setSellerRatings(prev => ({ ...prev, [sellerId]: data }));
        return data;
      }
    } catch (error) {
      console.error("Error fetching seller ratings:", error);
    }
    return null;
  };

  // Open rating modal
  const openRatingModal = (sellerId, sellerName, listing = null) => {
    setRatingTarget({ id: sellerId, name: sellerName, listing });
    setRatingValue(5);
    setRatingComment("");
    setShowRatingModal(true);
  };

  // Submit seller rating
  const handleSubmitRating = async () => {
    if (!token) {
      toast.error("Please login to rate sellers");
      return;
    }
    if (!ratingTarget) return;

    setSubmittingRating(true);
    try {
      const response = await fetch(`${API}/community/sellers/${ratingTarget.id}/rating`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          rating: ratingValue,
          comment: ratingComment
        })
      });
      if (response.ok) {
        toast.success("Rating submitted!");
        setShowRatingModal(false);
        // Refresh seller ratings
        fetchSellerRatings(ratingTarget.id);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit rating");
      }
    } catch (error) {
      toast.error("Failed to submit rating");
    } finally {
      setSubmittingRating(false);
    }
  };

  // Upload file for comment attachment
  const handleCommentFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Allowed: JPEG, PNG, GIF, WEBP, PDF");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 10MB");
      return;
    }

    if (commentAttachments.length >= 3) {
      toast.error("Maximum 3 attachments allowed");
      return;
    }

    setUploadingComment(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/community/posts/upload-image`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.image_url}`;
        setCommentAttachments(prev => [...prev, { url: fullUrl, name: file.name, type: file.type }]);
        toast.success("File uploaded!");
      }
    } catch (error) {
      toast.error("Failed to upload file");
    } finally {
      setUploadingComment(false);
      if (commentFileInputRef.current) commentFileInputRef.current.value = "";
    }
  };

  const fetchUserPosts = async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API}/community/posts?user_id=${user.id}&limit=50`);
      if (response.ok) {
        const data = await response.json();
        setUserPosts(data.posts);
      }
    } catch (error) {
      console.error("Error fetching user posts:", error);
    }
  };

  // Fetch marketplace posts
  const fetchMarketplacePosts = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/community/posts?category=Mini Related for Sale&limit=50`);
      if (response.ok) {
        const data = await response.json();
        return data.posts;
      }
      return [];
    } catch (error) {
      console.error("Error fetching marketplace:", error);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (viewMode === 'marketplace') {
      fetchMarketplacePosts().then(setPosts);
    } else if (viewMode !== 'profile') {
      fetchPosts();
    }
  }, [selectedCategory, page, viewMode]);

  useEffect(() => {
    if (viewMode === 'profile' && user) {
      fetchUserPosts();
    }
  }, [viewMode, user]);

  // Fetch seller ratings when a For Sale post is selected
  useEffect(() => {
    if (selectedPost?.category === 'Mini Related for Sale' && selectedPost?.user_id) {
      fetchSellerRatings(selectedPost.user_id);
    }
  }, [selectedPost?.id]);

  // Fetch seller ratings for marketplace posts
  useEffect(() => {
    if (viewMode === 'marketplace' && posts.length > 0) {
      // Get unique seller IDs
      const sellerIds = [...new Set(posts.map(p => p.user_id))];
      // Fetch ratings for each seller
      sellerIds.forEach(sellerId => {
        if (!sellerRatings[sellerId]) {
          fetchSellerRatings(sellerId);
        }
      });
    }
  }, [posts, viewMode]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Please login to create a post");
      return;
    }

    try {
      // Auto-set category for marketplace listings
      const postData = {
        ...newPost,
        category: viewMode === 'marketplace' ? 'Mini Related for Sale' : newPost.category,
        price: newPost.price ? parseFloat(newPost.price) : null,
        location: newPost.location || null,
        forsale_category: viewMode === 'marketplace' ? newPost.forsale_category : null,
        is_sold: false
      };

      const response = await fetch(`${API}/community/posts`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(postData)
      });

      if (response.ok) {
        toast.success("Topic created successfully!");
        setShowCreateModal(false);
        setNewPost({ title: "", content: "", category: "", image_urls: [], price: "", location: "", forsale_category: "" });
        fetchPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create topic");
      }
    } catch (error) {
      toast.error("Failed to create topic");
    }
  };

  const handleUpdatePost = async (e) => {
    e.preventDefault();
    if (!token || !editingPost) return;

    try {
      const response = await fetch(`${API}/community/posts/${editingPost.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editingPost.title,
          content: editingPost.content,
          category: editingPost.category,
          image_urls: editingPost.image_urls
        })
      });

      if (response.ok) {
        toast.success("Post updated successfully!");
        setEditingPost(null);
        fetchPosts();
        fetchUserPosts();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update post");
      }
    } catch (error) {
      toast.error("Failed to update post");
    }
  };

  const handleLike = async (postId) => {
    if (!token) {
      toast.error("Please login to like posts");
      return;
    }

    try {
      const response = await fetch(`${API}/community/posts/${postId}/like`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPosts(posts.map(p => 
          p.id === postId 
            ? { ...p, like_count: data.like_count, likes: data.liked ? [...(p.likes || []), user.id] : (p.likes || []).filter(id => id !== user.id) }
            : p
        ));
        if (selectedPost && selectedPost.id === postId) {
          setSelectedPost({
            ...selectedPost,
            like_count: data.like_count,
            likes: data.liked ? [...(selectedPost.likes || []), user.id] : (selectedPost.likes || []).filter(id => id !== user.id)
          });
        }
      }
    } catch (error) {
      toast.error("Failed to like post");
    }
  };

  const handleAddComment = async (postId) => {
    if (!token) {
      toast.error("Please login to comment");
      return;
    }
    if (!newComment.trim() && commentAttachments.length === 0) return;

    try {
      const response = await fetch(`${API}/community/posts/${postId}/comments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          content: newComment || "(attachment)",
          attachments: commentAttachments 
        })
      });

      if (response.ok) {
        const comment = await response.json();
        setSelectedPost({
          ...selectedPost,
          comments: [...(selectedPost.comments || []), comment],
          comment_count: (selectedPost.comment_count || 0) + 1
        });
        setNewComment("");
        setCommentAttachments([]);
        toast.success("Reply added!");
      }
    } catch (error) {
      toast.error("Failed to add reply");
    }
  };

  const handleEditComment = async (commentId) => {
    if (!token || !editCommentText.trim()) return;

    try {
      const response = await fetch(`${API}/community/comments/${commentId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ content: editCommentText })
      });

      if (response.ok) {
        const updatedComment = await response.json();
        setSelectedPost({
          ...selectedPost,
          comments: selectedPost.comments.map(c => c.id === commentId ? updatedComment : c)
        });
        setEditingComment(null);
        setEditCommentText("");
        toast.success("Reply updated!");
      }
    } catch (error) {
      toast.error("Failed to update reply");
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!token) return;

    try {
      const response = await fetch(`${API}/community/comments/${commentId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        setSelectedPost(prev => ({
          ...prev,
          comments: prev.comments.filter(c => c.id !== commentId),
          comment_count: Math.max(0, (prev.comment_count || 1) - 1)
        }));
        toast.success("Reply deleted!");
      }
    } catch (error) {
      toast.error("Failed to delete reply");
    }
  };

  const openDeleteConfirm = (type, id) => {
    setDeleteTarget({ type, id });
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (deleteTarget.type === 'post') {
      await executeDeletePost(deleteTarget.id);
    } else if (deleteTarget.type === 'comment') {
      await handleDeleteComment(deleteTarget.id);
    }
    setDeleteConfirmOpen(false);
    setDeleteTarget({ type: null, id: null });
  };

  const executeDeletePost = async (postId) => {
    if (!token) return;

    try {
      const response = await fetch(`${API}/community/posts/${postId}`, {
        method: "DELETE",
        headers: { "Authorization": `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success("Topic deleted");
        setSelectedPost(null);
        fetchPosts();
        fetchUserPosts();
      }
    } catch (error) {
      toast.error("Failed to delete topic");
    }
  };

  // Helper to resolve image URLs - handles both relative and absolute URLs
  const resolveImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    // Handle relative URLs like /api/uploads/...
    return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  };

  const openPostDetail = async (postId) => {
    try {
      const response = await fetch(`${API}/community/posts/${postId}`);
      if (response.ok) {
        const post = await response.json();
        setSelectedPost(post);
      }
    } catch (error) {
      toast.error("Failed to load topic");
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload JPEG, PNG, GIF, or WEBP");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 5MB");
      return;
    }

    if (newPost.image_urls.length >= 5) {
      toast.error("Maximum 5 images allowed");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/community/posts/upload-image`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.image_url}`;
        setNewPost({ ...newPost, image_urls: [...newPost.image_urls, fullUrl] });
        toast.success("Image uploaded!");
      }
    } catch (error) {
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  };

  // Profile Picture Component - Enhanced for prominence
  const ProfilePicture = ({ src, name, size = "md", className = "", showBorder = true }) => {
    const sizes = {
      xs: "h-6 w-6",
      sm: "h-10 w-10",
      md: "h-12 w-12",
      lg: "h-14 w-14",
      xl: "h-16 w-16",
      "2xl": "h-20 w-20",
      "3xl": "h-24 w-24"
    };
    
    const fontSizes = {
      xs: '10px',
      sm: '14px',
      md: '16px',
      lg: '18px',
      xl: '20px',
      '2xl': '24px',
      '3xl': '28px'
    };

    const borderStyles = showBorder ? "ring-2 ring-white shadow-md" : "";

    if (src) {
      const imgSrc = src.startsWith('http') ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`;
      return (
        <img 
          src={imgSrc}
          alt={name}
          className={`${sizes[size]} rounded-full object-cover ${borderStyles} hover:ring-4 hover:ring-gray-200 transition-all duration-200 ${className}`}
        />
      );
    }

    return (
      <div className={`${sizes[size]} rounded-full bg-gradient-to-br from-gray-700 to-gray-900 flex items-center justify-center ${borderStyles} hover:ring-4 hover:ring-gray-200 transition-all duration-200 ${className}`}>
        <span className="text-white font-bold" style={{ fontSize: fontSizes[size] }}>
          {name?.charAt(0)?.toUpperCase() || 'U'}
        </span>
      </div>
    );
  };

  // User Profile Panel
  const UserProfilePanel = () => (
    <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
      {/* Profile Header */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-8">
        <div className="flex items-center gap-6">
          <ProfilePicture src={user?.profile_image} name={user?.name} size="3xl" />
          <div className="text-white">
            <h2 className="text-2xl font-bold">{user?.name}</h2>
            <p className="text-gray-300 text-sm mt-1">{user?.email}</p>
            <p className="text-gray-400 text-xs mt-2">{t('memberSince')} {formatDate(user?.created_at || new Date().toISOString())}</p>
          </div>
        </div>
      </div>

      {/* Profile Stats */}
      <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">{userPosts.length}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">{t('topics')}</div>
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {userPosts.reduce((acc, p) => acc + (p.like_count || 0), 0)}
          </div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">{t('likes')}</div>
        </div>
        <div className="p-4 text-center">
          <div className="text-2xl font-bold text-gray-900">
            {userPosts.reduce((acc, p) => acc + (p.comment_count || 0), 0)}
          </div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">{t('replies')}</div>
        </div>
      </div>

      {/* User's Topics */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <MessageSquare className="h-4 w-4" />
          {t('yourTopics')}
        </h3>
        
        {userPosts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <MessageSquare className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>{t('noTopicsCreatedYet')}</p>
            <Button 
              onClick={() => { setViewMode('forum'); setShowCreateModal(true); }}
              className="mt-4 rounded-full"
              size="sm"
            >
              {t('createYourFirstTopic')}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {userPosts.map((post) => (
              <div 
                key={post.id}
                className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h4 
                      className="font-medium text-gray-900 truncate cursor-pointer hover:text-black"
                      onClick={() => { setViewMode('forum'); openPostDetail(post.id); }}
                    >
                      {post.title}
                    </h4>
                    <p className="text-sm text-gray-500 truncate">{post.content}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Heart className="h-3 w-3" /> {post.like_count || 0}
                      </span>
                      <span className="flex items-center gap-1">
                        <MessageCircle className="h-3 w-3" /> {post.comment_count || 0}
                      </span>
                      <span>{formatDate(post.created_at)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setEditingPost(post)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openDeleteConfirm('post', post.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Forum Topic Card
  const TopicCard = ({ post }) => (
    <div
      className="bg-white rounded-xl p-5 hover:shadow-lg transition-all cursor-pointer border border-gray-100 hover:border-gray-200"
      onClick={() => openPostDetail(post.id)}
      data-testid={`topic-card-${post.id}`}
    >
      <div className="flex gap-5">
        {/* Author Profile Picture - Enhanced Prominence */}
        <div className="flex-shrink-0 flex flex-col items-center gap-2">
          <div className="relative">
            <ProfilePicture src={post.user_profile_image} name={post.user_name} size="xl" />
            {/* Online indicator dot - decorative */}
            <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
          </div>
          <span className="text-xs font-semibold text-gray-700 text-center max-w-[70px] truncate">
            {post.user_name?.split(' ')[0]}
          </span>
        </div>

        {/* Topic Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className="inline-block px-2.5 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full mb-2">
                {post.category || "General"}
              </span>
              <h3 className="font-semibold text-gray-900 text-base line-clamp-1 hover:text-black">
                {post.title}
              </h3>
            </div>
            {post.image_urls?.length > 0 && (
              <div className="flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden bg-gray-100">
                <img src={resolveImageUrl(post.image_urls[0])} alt="" className="w-full h-full object-cover" />
              </div>
            )}
          </div>

          <p className="text-gray-600 text-sm line-clamp-2 mt-2">{post.content}</p>

          {/* Topic Meta */}
          <div className="flex items-center flex-wrap gap-4 mt-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {formatDate(post.created_at)}
            </span>
            <span className="flex items-center gap-1">
              <Heart className={`h-3.5 w-3.5 ${post.likes?.includes(user?.id) ? 'fill-red-500 text-red-500' : ''}`} />
              {post.like_count || 0} likes
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-3.5 w-3.5" />
              {post.comment_count || 0} replies
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="pt-24 pb-16 min-h-screen bg-gray-50" data-testid="mini-stripes-owners-gallery-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Large Profile Picture - Above Title */}
        {user && (
          <div className="flex justify-center mb-6">
            <Link to="/account" className="group">
              {user.profile_image ? (
                <img 
                  src={user.profile_image.startsWith('http') ? user.profile_image : `${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`} 
                  alt={user.name || 'Profile'} 
                  className="h-24 w-24 md:h-32 md:w-32 rounded-full object-cover border-4 border-gray-200 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:border-black group-hover:shadow-xl"
                />
              ) : (
                <div className="h-24 w-24 md:h-32 md:w-32 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-300 shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:border-black group-hover:shadow-xl">
                  <User className="h-12 w-12 md:h-16 md:w-16 text-gray-500" />
                </div>
              )}
            </Link>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h1 
            className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            {t('miniStripesOwnersForum')}
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('shareYourMini')}
          </p>
        </div>

        {/* Navigation Tabs - Big and Centered */}
        <div className="flex flex-col items-center gap-6 mb-8">
          {/* Main Navigation Tabs - Large and Centered */}
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {/* My Profile Tab */}
            {user && (
              <Button
                variant="ghost"
                onClick={() => { setViewMode('profile'); setSearchQuery(""); setSearchResults([]); setIsSearching(false); }}
                className={`rounded-full gap-3 px-8 py-6 text-lg font-semibold bg-black text-white hover:bg-gray-800 hover:text-white ${viewMode === 'profile' ? 'ring-4 ring-gray-400' : ''}`}
                data-testid="profile-view-btn"
              >
                <ProfilePicture src={user.profile_image} name={user.name} size="sm" className="ring-0" />
                {t('myProfile')}
              </Button>
            )}
            
            {/* Forum Tab */}
            <Button
              variant={viewMode === 'forum' && !isSearching ? 'default' : 'outline'}
              onClick={() => navigate('/forum')}
              className={`rounded-full gap-3 px-8 py-6 text-lg font-semibold ${viewMode === 'forum' ? 'ring-4 ring-black' : ''}`}
              data-testid="forum-view-btn"
            >
              <MessageSquare className="h-6 w-6" />
              {t('forum')}
            </Button>
            
            {/* Gallery Tab */}
            <Button
              onClick={() => navigate('/gallery')}
              className={`rounded-full gap-3 px-8 py-6 text-lg font-semibold bg-black text-white hover:bg-gray-800 ${viewMode === 'gallery' ? 'ring-4 ring-gray-400' : ''}`}
              data-testid="gallery-view-btn"
            >
              <Camera className="h-6 w-6" />
              {t('gallery')}
            </Button>

            {/* For Sale / Marketplace Tab */}
            <Button
              onClick={() => navigate('/for-sale')}
              className={`rounded-full gap-3 px-8 py-6 text-lg font-semibold ${viewMode === 'marketplace' ? 'bg-green-600 text-white hover:bg-green-700 ring-4 ring-green-400' : 'bg-green-500 text-white hover:bg-green-600'}`}
              data-testid="marketplace-view-btn"
            >
              <ShoppingBag className="h-6 w-6" />
              {t('forSale')}
            </Button>
          </div>

          {/* New Topic/Listing Button */}
          <Button
            onClick={() => user ? setShowCreateModal(true) : toast.error("Please login to create a topic")}
            className="rounded-full px-8 py-3 gap-2 text-base"
            data-testid="create-topic-btn"
          >
            <Plus className="h-5 w-5" />
            {viewMode === 'marketplace' ? t('newListing') : t('newTopic')}
          </Button>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative max-w-md mx-auto">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t('searchTopics')}
              className="w-full px-4 py-3 pl-12 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
              data-testid="search-topics-input"
            />
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            {searchQuery && (
              <button
                onClick={() => { setSearchQuery(""); setSearchResults([]); setIsSearching(false); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>

        {/* Search Results */}
        {isSearching && searchQuery && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Search Results for "{searchQuery}" ({searchResults.length} found)
            </h2>
            {searchResults.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-2xl">
                <p className="text-gray-500">No topics found matching your search.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {searchResults.map((post) => (
                  <TopicCard key={post.id} post={post} />
                ))}
              </div>
            )}
          </div>
        )}

        {!isSearching && viewMode === 'profile' && user ? (
          <UserProfilePanel />
        ) : !isSearching && viewMode === 'gallery' ? (
          /* Gallery View - Shows posts with images in a grid */
          <div>
            {/* Gallery Header - removed New Topic button */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Camera className="h-5 w-5" />
                {t('photoGallery')}
              </h2>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-12 w-12 animate-spin text-gray-400 mx-auto" />
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {posts.filter(p => p.image_urls?.length > 0).map((post) => (
                  <div 
                    key={post.id}
                    className="aspect-square rounded-xl overflow-hidden cursor-pointer group relative bg-gray-100"
                    onClick={() => openPostDetail(post.id)}
                  >
                    <img 
                      src={resolveImageUrl(post.image_urls[0])} 
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {/* Hover overlay with actions */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <p className="text-white text-sm font-medium line-clamp-2">{post.title}</p>
                        <p className="text-white/70 text-xs mt-1">by {post.user_name}</p>
                        <div className="flex items-center gap-4 mt-2 text-white/90 text-xs">
                          <span className="flex items-center gap-1">
                            <Heart className={`h-4 w-4 ${post.likes?.includes(user?.id) ? 'fill-red-500 text-red-500' : ''}`} /> 
                            {post.like_count || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageCircle className="h-4 w-4" /> {post.comment_count || 0}
                          </span>
                          {user && (user.id === post.user_id || user.is_admin) && (
                            <button 
                              onClick={(e) => { e.stopPropagation(); openDeleteConfirm('post', post.id); }}
                              className="flex items-center gap-1 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Multiple images indicator */}
                    {post.image_urls.length > 1 && (
                      <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                        <Image className="h-3 w-3" />
                        {post.image_urls.length}
                      </div>
                    )}
                  </div>
                ))}
                {posts.filter(p => p.image_urls?.length > 0).length === 0 && (
                  <div className="col-span-full text-center py-12 bg-white rounded-2xl">
                    <Camera className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-4">{t('noPhotosYet')}</p>
                    <Button 
                      onClick={() => navigate('/forum')} 
                      className="rounded-full"
                    >
                      {t('goToForum')}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : !isSearching && viewMode === 'marketplace' ? (
          /* Marketplace View - For Sale Listings */
          <div>
            {/* Marketplace Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-green-600" />
                {t('miniRelatedForSale')}
              </h2>
              <Button 
                onClick={() => {
                  if (!user) {
                    toast.error("Please login to create a listing");
                    return;
                  }
                  setNewPost({ ...newPost, category: "Mini Related for Sale" });
                  setShowCreateModal(true);
                }}
                className="rounded-full gap-2 bg-green-600 hover:bg-green-700"
                size="sm"
              >
                <Plus className="h-4 w-4" />
                {t('newListing')}
              </Button>
            </div>

            {/* Marketplace Info Banner */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6">
              <p className="text-green-800 text-sm">
                <strong>{t('welcomeToMarketplace')}</strong> {t('buyAndSellMini')}
              </p>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="h-12 w-12 animate-spin text-gray-400 mx-auto" />
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl">
                <ShoppingBag className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg mb-4">{t('noListingsYet')}</p>
                <Button 
                  onClick={() => {
                    if (!user) {
                      toast.error("Please login to create a listing");
                      return;
                    }
                    setNewPost({ ...newPost, category: "Mini Related for Sale" });
                    setShowCreateModal(true);
                  }} 
                  className="rounded-full bg-green-600 hover:bg-green-700"
                >
                  {t('createFirstListing')}
                </Button>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {posts.map((post) => (
                  <div 
                    key={post.id}
                    className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow cursor-pointer border border-gray-100"
                    onClick={() => openPostDetail(post.id)}
                    data-testid={`marketplace-listing-${post.id}`}
                  >
                    {/* Image */}
                    {post.image_urls?.length > 0 ? (
                      <div className="aspect-[4/3] relative">
                        <img 
                          src={resolveImageUrl(post.image_urls[0])} 
                          alt={post.title}
                          className="w-full h-full object-cover"
                        />
                        {/* SOLD Banner */}
                        {post.is_sold && (
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <div className="bg-red-600 text-white text-xl font-bold px-6 py-2 rounded-full transform -rotate-12 shadow-lg">
                              SOLD
                            </div>
                          </div>
                        )}
                        {post.image_urls.length > 1 && !post.is_sold && (
                          <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <Image className="h-3 w-3" />
                            {post.image_urls.length}
                          </div>
                        )}
                        {/* Category badge */}
                        {post.forsale_category && !post.is_sold && (
                          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            {post.forsale_category}
                          </div>
                        )}
                        {/* Price badge - from price field or extracted from content */}
                        {!post.is_sold && (post.price ? (
                          <div className="absolute bottom-2 left-2 bg-green-600 text-white text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
                            <PoundSterling className="h-3.5 w-3.5" />
                            {post.price.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        ) : post.content.match(/£[\d,]+(\.\d{2})?/) ? (
                          <div className="absolute bottom-2 left-2 bg-green-600 text-white text-sm font-bold px-3 py-1.5 rounded-full">
                            {post.content.match(/£[\d,]+(\.\d{2})?/)[0]}
                          </div>
                        ) : (
                          <div className="absolute bottom-2 left-2 bg-gray-600 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                            Price on request
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="aspect-[4/3] bg-gray-100 flex items-center justify-center relative">
                        <ShoppingBag className="h-12 w-12 text-gray-300" />
                        {/* SOLD Banner for no-image listings */}
                        {post.is_sold && (
                          <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <div className="bg-red-600 text-white text-xl font-bold px-6 py-2 rounded-full transform -rotate-12 shadow-lg">
                              SOLD
                            </div>
                          </div>
                        )}
                        {/* Category badge */}
                        {post.forsale_category && !post.is_sold && (
                          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                            <Tag className="h-3 w-3" />
                            {post.forsale_category}
                          </div>
                        )}
                        {/* Price badge even without image */}
                        {!post.is_sold && (post.price ? (
                          <div className="absolute bottom-2 left-2 bg-green-600 text-white text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-1">
                            <PoundSterling className="h-3.5 w-3.5" />
                            {post.price.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        ) : (
                          <div className="absolute bottom-2 left-2 bg-gray-600 text-white text-xs font-medium px-3 py-1.5 rounded-full">
                            Price on request
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 line-clamp-1 mb-1">{post.title}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2 mb-2">{post.content}</p>
                      
                      {/* Location */}
                      {post.location && (
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-3">
                          <MapPin className="h-3 w-3" />
                          <span>{post.location}</span>
                        </div>
                      )}
                      
                      {/* Meta */}
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-2">
                          <ProfilePicture src={post.user_profile_image} name={post.user_name} size="xs" />
                          <span>{post.user_name}</span>
                          {sellerRatings[post.user_id] && sellerRatings[post.user_id].total_ratings > 0 && (
                            <span className="flex items-center gap-0.5 text-amber-500 bg-amber-50 px-1.5 py-0.5 rounded-full">
                              <Star className="h-3 w-3 fill-amber-400" />
                              {sellerRatings[post.user_id].average_rating.toFixed(1)}
                            </span>
                          )}
                        </div>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(post.created_at)}
                        </span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <MessageCircle className="h-3 w-3" /> {post.comment_count || 0} replies
                        </span>
                        {user && (user.id === post.user_id || user.is_admin) && (
                          <button 
                            onClick={(e) => { e.stopPropagation(); openDeleteConfirm('post', post.id); }}
                            className="flex items-center gap-1 text-red-500 hover:text-red-700 ml-auto"
                          >
                            <Trash2 className="h-3 w-3" /> Delete
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : !isSearching && (
          <>
            {/* Forum View - Topics List */}
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left Side - Category Filter Dropdown Tabs */}
              <div className="lg:w-64 flex-shrink-0">
                <div className="bg-white rounded-2xl p-4 shadow-sm lg:sticky lg:top-28">
                  <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">{t('filterByTopic')}</h3>
                  
                  {/* Dropdown-style Category Pills */}
                  <div className="space-y-2">
                    {/* All Topics */}
                    <button
                      onClick={() => setSelectedCategory("")}
                      className={`w-full text-left px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === "" 
                          ? 'bg-black text-white shadow-sm' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                      data-testid="filter-all-topics"
                    >
                      {t('allTopics')}
                    </button>
                    
                    {/* Category Pills */}
                    {getAllCategories().filter(cat => cat !== "Mini Related for Sale").map((cat) => (
                      <button
                        key={cat}
                        onClick={() => setSelectedCategory(cat)}
                        className={`w-full text-left px-4 py-2.5 rounded-full text-sm font-medium transition-all ${
                          selectedCategory === cat 
                            ? 'bg-black text-white shadow-sm' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {translateCategory(cat)}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Side - Topics List */}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    {selectedCategory ? translateCategory(selectedCategory) : t('allTopics')}
                  </h2>
                </div>

                {/* Topics List */}
                {loading ? (
                  <div className="text-center py-12">
                    <Loader2 className="h-12 w-12 animate-spin text-gray-400 mx-auto" />
                  </div>
                ) : posts.filter(p => !selectedCategory || p.category === selectedCategory).length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-2xl">
                    <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg mb-4">
                      {selectedCategory ? `No topics in "${translateCategory(selectedCategory)}" yet.` : t('noTopicsYet')}
                    </p>
                    <Button onClick={() => setShowCreateModal(true)} className="rounded-full">
                      {t('createFirstTopic')}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {posts
                      .filter(p => !selectedCategory || p.category === selectedCategory)
                      .map((post) => (
                        <TopicCard key={post.id} post={post} />
                      ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                      className="rounded-full"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="flex items-center px-4 text-sm text-gray-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                      className="rounded-full"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Create Topic Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">
                  {viewMode === 'marketplace' || newPost.category === 'Mini Related for Sale' 
                    ? t('createNewListing') 
                    : t('createNewTopic')}
                </h2>
                <button onClick={() => { setShowCreateModal(false); setNewPost({ title: "", content: "", category: "", image_urls: [], price: "", location: "", forsale_category: "" }); }} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleCreatePost} className="space-y-4">
                {/* Author Preview */}
                {user && (
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <ProfilePicture src={user.profile_image} name={user.name} size="md" />
                    <div>
                      <div className="font-medium text-gray-900">{user.name}</div>
                      <div className="text-xs text-gray-500">{t('postingAs')}</div>
                    </div>
                  </div>
                )}

                {/* Marketplace info banner */}
                {(viewMode === 'marketplace' || newPost.category === 'Mini Related for Sale') && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-sm text-green-800">
                    <strong>{t('sellingTips')}</strong> {t('sellingTipsText')}
                  </div>
                )}

                {/* Category - Only show for non-marketplace posts */}
                {viewMode !== 'marketplace' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('category')}</label>
                    {!showAddCategory ? (
                      <div className="space-y-2">
                        <select
                          value={newPost.category}
                          onChange={(e) => setNewPost({ ...newPost, category: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                          required
                          data-testid="topic-category-select"
                        >
                          <option value="">{t('selectCategory')}</option>
                          {getAllCategories().map((cat) => (
                            <option key={cat} value={cat}>{translateCategory(cat)}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => setShowAddCategory(true)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                        >
                          <Plus className="h-3 w-3" />
                          {t('newTopicCategory')}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={newCustomCategory}
                            onChange={(e) => setNewCustomCategory(e.target.value)}
                            placeholder={t('enterNewCategoryName')}
                            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                            autoFocus
                          />
                          <Button
                            type="button"
                            onClick={handleAddCustomCategory}
                            disabled={!newCustomCategory.trim()}
                            className="rounded-xl"
                          >
                            {t('add')}
                          </Button>
                        </div>
                        <button
                          type="button"
                          onClick={() => { setShowAddCategory(false); setNewCustomCategory(""); }}
                          className="text-sm text-gray-500 hover:text-gray-700"
                        >
                          {t('cancelUseExisting')}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* For Sale Category - Only for marketplace */}
                {viewMode === 'marketplace' && (
                  <div>
                    <label className="block text-sm font-medium mb-1">{t('category')} <span className="text-red-500">*</span></label>
                    <select
                      value={newPost.forsale_category}
                      onChange={(e) => setNewPost({ ...newPost, forsale_category: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                      required
                      data-testid="forsale-category-select"
                    >
                      <option value="">{t('selectCategory')}</option>
                      {forsaleCategories.map((cat) => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium mb-1">{t('title')}</label>
                  <input
                    type="text"
                    value={newPost.title}
                    onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                    placeholder={t('titlePlaceholder')}
                    required
                    data-testid="topic-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">{t('content')}</label>
                  <textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black resize-none"
                    rows={4}
                    placeholder={t('contentPlaceholder')}
                    required
                    data-testid="topic-content-input"
                  />
                </div>

                {/* Price Input - Only for Marketplace listings */}
                {(viewMode === 'marketplace' || newPost.category === 'Mini Related for Sale') && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('priceLabel')}</label>
                      <div className="relative">
                        <PoundSterling className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={newPost.price}
                          onChange={(e) => setNewPost({ ...newPost, price: e.target.value })}
                          className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                          placeholder={t('pricePlaceholder')}
                          data-testid="listing-price-input"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{t('priceHelp')}</p>
                    </div>

                    {/* Location Input */}
                    <div>
                      <label className="block text-sm font-medium mb-1">{t('locationLabel')}</label>
                      <input
                        type="text"
                        value={newPost.location}
                        onChange={(e) => setNewPost({ ...newPost, location: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                        placeholder={t('locationPlaceholder')}
                        data-testid="listing-location-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('locationHelp')}</p>
                    </div>
                  </>
                )}

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">{t('photosOptional')}</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading || newPost.image_urls.length >= 5}
                      className="flex-1 rounded-xl gap-2"
                    >
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {uploading ? t('uploading') : t('uploadPhoto')}
                    </Button>
                  </div>

                  {newPost.image_urls.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {newPost.image_urls.map((url, idx) => (
                        <div key={idx} className="relative">
                          <img src={url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                          <button
                            type="button"
                            onClick={() => setNewPost({ ...newPost, image_urls: newPost.image_urls.filter((_, i) => i !== idx) })}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Button type="submit" className="w-full rounded-full py-3" data-testid="submit-topic-btn">
                  {viewMode === 'marketplace' || newPost.category === 'Mini Related for Sale' 
                    ? t('postListing') 
                    : t('postTopic')}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Post Modal */}
      {editingPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">Edit Topic</h2>
                <button onClick={() => setEditingPost(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <form onSubmit={handleUpdatePost} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    type="text"
                    value={editingPost.title}
                    onChange={(e) => setEditingPost({ ...editingPost, title: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Content</label>
                  <textarea
                    value={editingPost.content}
                    onChange={(e) => setEditingPost({ ...editingPost, content: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black resize-none"
                    rows={4}
                    required
                  />
                </div>

                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setEditingPost(null)} className="flex-1 rounded-full">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 rounded-full">
                    Save Changes
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Topic Detail Modal */}
      {selectedPost && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <span className="inline-block px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
                  {selectedPost.category}
                </span>
                <button onClick={() => setSelectedPost(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="h-6 w-6" />
                </button>
              </div>

              {/* Author Info - Enhanced */}
              <div className="flex items-center gap-4 mb-4 p-4 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-100">
                <ProfilePicture src={selectedPost.user_profile_image} name={selectedPost.user_name} size="xl" />
                <div>
                  <div className="font-bold text-gray-900 text-lg">{selectedPost.user_name}</div>
                  <div className="text-sm text-gray-500 flex items-center gap-2">
                    <Clock className="h-3.5 w-3.5" />
                    {formatDate(selectedPost.created_at)}
                  </div>
                </div>
              </div>

              {/* Images */}
              {selectedPost.image_urls?.length > 0 && (
                <div className="mb-6">
                  {selectedPost.image_urls.length === 1 ? (
                    <img 
                      src={resolveImageUrl(selectedPost.image_urls[0])} 
                      alt="" 
                      className="w-full rounded-xl cursor-pointer"
                      onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}
                    />
                  ) : (
                    <div className="grid grid-cols-2 gap-2 rounded-xl overflow-hidden">
                      {selectedPost.image_urls.slice(0, 4).map((url, idx) => (
                        <div key={idx} className="relative">
                          <img 
                            src={resolveImageUrl(url)} 
                            alt="" 
                            className="w-full h-48 object-cover cursor-pointer"
                            onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                          />
                          {idx === 3 && selectedPost.image_urls.length > 4 && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <span className="text-white text-xl font-bold">+{selectedPost.image_urls.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <h2 className="text-2xl font-bold mb-3">{selectedPost.title}</h2>
              
              {/* SOLD Banner */}
              {selectedPost.is_sold && selectedPost.category === 'Mini Related for Sale' && (
                <div className="flex items-center gap-3 mb-4 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-red-700">SOLD</p>
                    <p className="text-xs text-red-600">This item has been sold</p>
                  </div>
                </div>
              )}

              {/* Category Badge for For Sale */}
              {selectedPost.category === 'Mini Related for Sale' && selectedPost.forsale_category && (
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
                    <Tag className="h-3 w-3" />
                    {selectedPost.forsale_category}
                  </span>
                </div>
              )}
              
              {/* Price Display for Marketplace Listings */}
              {(selectedPost.category === 'Mini Related for Sale' && !selectedPost.is_sold && (selectedPost.price || selectedPost.content.match(/£[\d,]+(\.\d{2})?/))) && (
                <div className="flex items-center gap-3 mb-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center">
                      <PoundSterling className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="text-xs text-green-700 font-medium uppercase tracking-wide">Asking Price</p>
                      <p className="text-2xl font-bold text-green-800">
                        {selectedPost.price 
                          ? `£${selectedPost.price.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                          : selectedPost.content.match(/£[\d,]+(\.\d{2})?/)?.[0] || 'Price on request'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Location Display */}
              {selectedPost.category === 'Mini Related for Sale' && selectedPost.location && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-xl">
                  <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                    <MapPin className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Location</p>
                    <p className="text-sm font-semibold text-gray-800">{selectedPost.location}</p>
                  </div>
                </div>
              )}

              {/* Seller Rating Display */}
              {selectedPost.category === 'Mini Related for Sale' && (
                <div className="flex items-center gap-3 mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                  <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center">
                    <Star className="h-4 w-4 text-white fill-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-amber-700 font-medium uppercase tracking-wide">Seller Rating</p>
                    {sellerRatings[selectedPost.user_id] ? (
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <Star 
                              key={star}
                              className={`h-4 w-4 ${
                                star <= Math.round(sellerRatings[selectedPost.user_id].average_rating)
                                  ? 'text-amber-400 fill-amber-400'
                                  : 'text-gray-300'
                              }`} 
                            />
                          ))}
                        </div>
                        <span className="text-sm font-semibold text-gray-800">
                          {sellerRatings[selectedPost.user_id].average_rating.toFixed(1)}
                        </span>
                        <span className="text-xs text-gray-500">
                          ({sellerRatings[selectedPost.user_id].total_ratings} {sellerRatings[selectedPost.user_id].total_ratings === 1 ? 'rating' : 'ratings'})
                        </span>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">No ratings yet</p>
                    )}
                  </div>
                  {/* Rate Seller Button */}
                  {user && user.id !== selectedPost.user_id && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openRatingModal(selectedPost.user_id, selectedPost.user_name, selectedPost)}
                      className="border-amber-400 text-amber-700 hover:bg-amber-100 rounded-full"
                      data-testid="rate-seller-btn"
                    >
                      <Star className="h-3 w-3 mr-1" />
                      Rate
                    </Button>
                  )}
                </div>
              )}

              {/* Message Seller Button */}
              {selectedPost.category === 'Mini Related for Sale' && user && user.id !== selectedPost.user_id && !selectedPost.is_sold && (
                <Button
                  onClick={() => openMessageModal(selectedPost.user_id, selectedPost.user_name, selectedPost)}
                  className="w-full mb-4 rounded-xl bg-blue-600 hover:bg-blue-700"
                  data-testid="message-seller-btn"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Message Seller
                </Button>
              )}
              
              <p className="text-gray-700 whitespace-pre-wrap mb-6">{selectedPost.content}</p>

              {/* Actions */}
              <div className="flex items-center gap-4 pb-6 border-b border-gray-100">
                <button
                  onClick={() => handleLike(selectedPost.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
                    selectedPost.likes?.includes(user?.id)
                      ? 'bg-red-50 text-red-500'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid="like-btn"
                >
                  <Heart className={`h-5 w-5 ${selectedPost.likes?.includes(user?.id) ? 'fill-red-500' : ''}`} />
                  {selectedPost.like_count || 0}
                </button>
                <span className="flex items-center gap-2 text-gray-500">
                  <MessageCircle className="h-5 w-5" />
                  {selectedPost.comment_count || 0} replies
                </span>
                {/* Mark as Sold button for For Sale listings */}
                {user && (user.id === selectedPost.user_id || user.is_admin) && selectedPost.category === 'Mini Related for Sale' && (
                  <button
                    onClick={() => handleMarkSold(selectedPost.id, selectedPost.is_sold)}
                    className={`ml-auto px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                      selectedPost.is_sold 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                    data-testid="mark-sold-btn"
                  >
                    <CheckCircle className="h-4 w-4 inline mr-1" />
                    {selectedPost.is_sold ? 'Mark Available' : 'Mark as Sold'}
                  </button>
                )}
                {user && (user.id === selectedPost.user_id || user.is_admin) && (
                  <button
                    onClick={() => openDeleteConfirm('post', selectedPost.id)}
                    className={`${selectedPost.category !== 'Mini Related for Sale' ? 'ml-auto' : ''} p-2 text-red-500 hover:bg-red-50 rounded-full`}
                    data-testid="delete-post-btn"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* Replies */}
              <div className="mt-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <MessageCircle className="h-5 w-5" />
                  Replies
                </h3>
                
                {selectedPost.comments?.length > 0 ? (
                  <div className="space-y-4 mb-6">
                    {selectedPost.comments.map((comment) => (
                      <div key={comment.id} className="flex gap-4">
                        <ProfilePicture src={comment.user_profile_image} name={comment.user_name} size="md" />
                        <div className="flex-1 bg-gray-50 rounded-xl p-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-sm text-gray-900">{comment.user_name}</span>
                              <span className="text-xs text-gray-400">{formatDate(comment.created_at)}</span>
                              {comment.updated_at && <span className="text-xs text-gray-400 italic">(edited)</span>}
                            </div>
                            {user && (user.id === comment.user_id || user.is_admin) && editingComment !== comment.id && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => { setEditingComment(comment.id); setEditCommentText(comment.content); }}
                                  className="p-1 text-gray-400 hover:text-blue-600 rounded"
                                >
                                  <Edit2 className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => openDeleteConfirm('comment', comment.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 rounded"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                          
                          {editingComment === comment.id ? (
                            <div className="flex gap-2 items-center">
                              <input
                                type="text"
                                value={editCommentText}
                                onChange={(e) => setEditCommentText(e.target.value)}
                                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black text-sm"
                                autoFocus
                              />
                              <button onClick={() => handleEditComment(comment.id)} className="p-2 bg-black text-white rounded-full">
                                <Check className="h-3 w-3" />
                              </button>
                              <button onClick={() => { setEditingComment(null); setEditCommentText(""); }} className="p-2 bg-gray-200 rounded-full">
                                <X className="h-3 w-3" />
                              </button>
                            </div>
                          ) : (
                            <>
                              <p className="text-gray-700 text-sm">{comment.content}</p>
                              {/* Display attachments */}
                              {comment.attachments?.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {comment.attachments.map((att, idx) => (
                                    <a
                                      key={idx}
                                      href={att.url.startsWith('http') ? att.url : `${process.env.REACT_APP_BACKEND_URL}${att.url}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className={`flex items-center gap-1 px-2 py-1 rounded-lg text-xs ${
                                        att.type === 'application/pdf' 
                                          ? 'bg-red-50 text-red-700 hover:bg-red-100' 
                                          : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                      }`}
                                    >
                                      {att.type === 'application/pdf' ? (
                                        <FileText className="h-3 w-3" />
                                      ) : (
                                        <Image className="h-3 w-3" />
                                      )}
                                      {att.name || 'Attachment'}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm mb-6">No replies yet. Be the first to reply!</p>
                )}

                {/* Add Reply with attachments */}
                {user ? (
                  <div className="space-y-2">
                    {/* Attachment previews */}
                    {commentAttachments.length > 0 && (
                      <div className="flex flex-wrap gap-2 ml-11">
                        {commentAttachments.map((att, idx) => (
                          <div key={idx} className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-lg text-xs">
                            {att.type === 'application/pdf' ? (
                              <FileText className="h-3 w-3 text-red-600" />
                            ) : (
                              <Image className="h-3 w-3 text-blue-600" />
                            )}
                            <span className="max-w-[100px] truncate">{att.name}</span>
                            <button
                              onClick={() => setCommentAttachments(prev => prev.filter((_, i) => i !== idx))}
                              className="p-0.5 hover:text-red-600"
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex gap-3">
                      <ProfilePicture src={user.profile_image} name={user.name} size="sm" />
                      <div className="flex-1 flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            className="w-full px-4 py-3 pr-12 rounded-full border border-gray-200 focus:outline-none focus:ring-2 focus:ring-black"
                            placeholder="Write a reply..."
                            data-testid="reply-input"
                          />
                          <input
                            type="file"
                            ref={commentFileInputRef}
                            onChange={handleCommentFileUpload}
                            accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                            className="hidden"
                          />
                          <button
                            onClick={() => commentFileInputRef.current?.click()}
                            disabled={uploadingComment || commentAttachments.length >= 3}
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                            title="Attach file (max 3)"
                          >
                            {uploadingComment ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Paperclip className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                        <Button
                          onClick={() => handleAddComment(selectedPost.id)}
                          className="rounded-full px-4"
                          disabled={!newComment.trim() && commentAttachments.length === 0}
                          data-testid="submit-reply-btn"
                        >
                          <Send className="h-5 w-5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 text-sm">
                    <Link to="/login" className="text-black font-medium hover:underline">Login</Link> to reply
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Lightbox */}
      {lightboxOpen && selectedPost?.image_urls && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-[60]" onClick={() => setLightboxOpen(false)}>
          <button onClick={() => setLightboxOpen(false)} className="absolute top-4 right-4 text-white/80 hover:text-white p-2">
            <X className="h-8 w-8" />
          </button>
          
          {selectedPost.image_urls.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => prev === 0 ? selectedPost.image_urls.length - 1 : prev - 1); }}
                className="absolute left-4 text-white/80 hover:text-white p-2 bg-black/30 rounded-full"
              >
                <ChevronLeft className="h-8 w-8" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIndex(prev => prev === selectedPost.image_urls.length - 1 ? 0 : prev + 1); }}
                className="absolute right-4 text-white/80 hover:text-white p-2 bg-black/30 rounded-full"
              >
                <ChevronRight className="h-8 w-8" />
              </button>
            </>
          )}
          
          <img src={resolveImageUrl(selectedPost.image_urls[lightboxIndex])} alt="" className="max-h-[90vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
          
          {selectedPost.image_urls.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black/50 px-4 py-2 rounded-full">
              {lightboxIndex + 1} / {selectedPost.image_urls.length}
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteTarget.type === 'post' ? 'Delete Topic' : 'Delete Reply'}</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Message Seller Modal */}
      {showMessageModal && messageRecipient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowMessageModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Message {messageRecipient.name}</h2>
              <button onClick={() => setShowMessageModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {messageListing && (
              <div className="mb-4 p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Regarding:</p>
                <p className="text-sm font-medium text-gray-800">{messageListing.title}</p>
                {messageListing.price && (
                  <p className="text-sm text-green-600">£{messageListing.price.toLocaleString('en-GB', { minimumFractionDigits: 2 })}</p>
                )}
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Your Message</label>
              <textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={4}
                placeholder="Hi, I'm interested in this item..."
                data-testid="message-content-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowMessageModal(false)}
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={!messageContent.trim()}
                className="flex-1 rounded-xl bg-blue-600 hover:bg-blue-700"
                data-testid="send-message-btn"
              >
                <Send className="h-4 w-4 mr-2" />
                Send Message
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rate Seller Modal */}
      {showRatingModal && ratingTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowRatingModal(false)}>
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Rate {ratingTarget.name}</h2>
              <button onClick={() => setShowRatingModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {ratingTarget.listing && (
              <div className="mb-4 p-3 bg-gray-50 rounded-xl">
                <p className="text-xs text-gray-500 mb-1">Regarding:</p>
                <p className="text-sm font-medium text-gray-800">{ratingTarget.listing.title}</p>
              </div>
            )}
            
            {/* Star Rating */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Your Rating</label>
              <div className="flex items-center justify-center gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRatingValue(star)}
                    className="p-1 transition-transform hover:scale-110"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`h-8 w-8 ${
                        star <= ratingValue
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-gray-300 hover:text-amber-200'
                      }`} 
                    />
                  </button>
                ))}
              </div>
              <p className="text-center text-sm text-gray-500 mt-2">
                {ratingValue === 5 ? 'Excellent!' : 
                 ratingValue === 4 ? 'Very Good' : 
                 ratingValue === 3 ? 'Good' : 
                 ratingValue === 2 ? 'Fair' : 'Poor'}
              </p>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Review (Optional)</label>
              <textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                rows={3}
                placeholder="Share your experience with this seller..."
                data-testid="rating-comment-input"
              />
            </div>
            
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRatingModal(false)}
                className="flex-1 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitRating}
                disabled={submittingRating}
                className="flex-1 rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="submit-rating-btn"
              >
                {submittingRating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Star className="h-4 w-4 mr-2" />
                )}
                Submit Rating
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MiniStripesOwnersClub;
